import API_BASE_URL from '../config/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('token');
  }

  // Token beállítása
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  // Token lekérése
  getToken() {
    return this.token || localStorage.getItem('token');
  }

  // Headers összeállítása - FRISSÍTETT
  getHeaders(options = {}) {
    const headers = {};

    // Ha nem FormData, akkor állítsuk be a Content-Type-ot
    if (!options.isFormData) {
      headers['Content-Type'] = options.contentType || 'application/json';
    }

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Access token megújítása a httpOnly refresh token cookie alapján.
  // Egyetlen folyamatban lévő refresh-hívást tartunk fenn, hogy párhuzamos 401-ek
  // ne versenyezzenek egymással a (rotáló) refresh tokenért.
  async refreshAccessToken() {
    if (!this._refreshPromise) {
      this._refreshPromise = (async () => {
        try {
          const response = await fetch(`${this.baseURL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
          });
          const data = await response.json();

          if (response.ok && data.success && data.data?.token) {
            this.setToken(data.data.token);
            return true;
          }
          this.setToken(null);
          return false;
        } catch (error) {
          this.setToken(null);
          return false;
        } finally {
          this._refreshPromise = null;
        }
      })();
    }
    return this._refreshPromise;
  }

  // Alapvető fetch wrapper - FRISSÍTETT
  async request(endpoint, options = {}, _isRetry = false) {
    const url = `${this.baseURL}${endpoint}`;

    const isFormData = options.body instanceof FormData;

    const config = {
      method: options.method || 'GET',
      headers: this.getHeaders({
        isFormData,
        contentType: options.contentType
      }),
      credentials: 'include',
      ...options
    };

    // Ha nem FormData és van body, akkor JSON.stringify
    if (config.body && !isFormData && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      let logBody = '';
      if (isFormData) {
        logBody = 'FormData';
      } else if (typeof config.body === 'string') {
        try {
          logBody = JSON.parse(config.body);
        } catch (e) {
          logBody = config.body;
        }
      } else {
        logBody = config.body;
      }

      console.log(`API Request: ${config.method} ${url}`, logBody);
      // ÚJ DEBUG LOG: Mi van a config.body-ban közvetlenül a fetch előtt?
      console.log(`DEBUG: Raw config.body (stringified) before fetch:`, config.body); 
      // ÚJ DEBUG LOG: Mi van a config.headers-ben?
      console.log(`DEBUG: Headers before fetch:`, config.headers);

      const response = await fetch(url, config);
      const data = await response.json();

      console.log(`API Response: ${response.status}`, data);

      if (!response.ok) {
        const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/refresh') || endpoint.startsWith('/auth/register');

        if (response.status === 401 && !_isRetry && !isAuthEndpoint && this.getToken()) {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            return this.request(endpoint, options, true);
          }
        }

        // Egyes backend-válaszok (rate limiter, globális hibakezelő) csak `error` mezőt küldenek,
        // vagy semmilyen szöveget — ilyenkor érthető magyar tartalék üzenet kell, nem nyers HTTP-kód
        const serverText = typeof data.message === 'string' && data.message
          ? data.message
          : (typeof data.error === 'string' && data.error ? data.error : null);
        const fallbackText = response.status === 429
          ? 'Túl sok kérés, próbálja újra néhány perc múlva.'
          : response.status >= 500
            ? 'Szerver hiba történt, próbálja újra később.'
            : `Hiba történt a kérés feldolgozása során (${response.status}).`;
        const error = new Error(serverText || fallbackText);
        error.status = response.status;
        error.data = data;

        if (data.errors) {
          error.details = data.errors;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      if (error.status && error.data) {
        throw error;
      }
      throw new Error(`Hiba történt a kérés során: ${error.message}`);
    }
  }

  // GET kérés
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET'
    });
  }

  // POST kérés - FRISSÍTETT
  async post(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data,
      ...options
    });
  }

  // PUT kérés - FRISSÍTETT
  async put(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data,
      ...options
    });
  }

  // DELETE kérés
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  // PATCH kérés - FRISSÍTETT
  async patch(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data,
      ...options
    });
  }

  // File upload helper - ÚJ
  async uploadFile(endpoint, file, additionalData = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // További adatok hozzáadása
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    return this.post(endpoint, formData);
  }

  // Avatar upload helper - ÚJ
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);
    
    return this.post('/users/avatar', formData);
  }
}

// Singleton instance
const apiService = new ApiService();
export default apiService;